<template>
  <v-container>
    <back-button :label="transPlural('settlement.settlement')"></back-button>
    <page-heading>{{ pageTitle }}</page-heading>

    <div class="pb-5">
      <v-row>
        <v-col cols="5">
          <v-card elevation="1">
            <v-card-text>
              <div class="mt-3 mb-6">
                <date-select
                  v-model="dateInternal"
                  type="month"
                  :label="trans('general.monthYear')"
                  :clearable="false"
                />
              </div>
              <v-btn color="primary" @click="generatePreview" :loading="generatingPreview">
                <v-icon left>mdi-magnify-plus-outline</v-icon>
                {{ trans('settlement.generatePreview') }}
              </v-btn>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <template v-if="settlementPreview">
        <v-divider class="mt-8"></v-divider>
        <div class="mt-8" style="font-size: 1.8em;">{{ pageSubTitle }}</div>
        <v-row class="pb-9 pt-7">
          <v-col>
            <stat-number :label="trans('settlement.commissionNet')">
              <currency-amount :amount="settlementPreview.provisionNet"></currency-amount>
            </stat-number>
          </v-col>
          <v-col>
            <stat-number :label="trans('settlement.commissionGross')">
              <currency-amount :amount="settlementPreview.provisionGross"></currency-amount>
            </stat-number>
          </v-col>
          <v-col>
            <stat-number :label="trans('settlement.saldoTotalWithOpenBookings')">
              <currency-amount :amount="settlementPreview.saldoTotal"></currency-amount>
            </stat-number>
          </v-col>
          <v-col class="d">
            <stat-number :label="transPlural('object.object')">
              {{ settlementPreview.settlementObjects.length }}
            </stat-number>
          </v-col>
        </v-row>

        <v-data-table
          :headers="[
            {text: transSingular('object.object'), value: 'object.name'},
            {text: trans('settlement.commissionNet'), value: 'provisionNet', type: 'currency'},
            {text: trans('settlement.commissionGross'), value: 'provisionGross', type: 'currency'},
            {text: trans('settlement.saldoTotalWithOpenBookings'), value: 'saldo', type: 'currency'},
            {text: '', value: 'actions'},
          ]"
          :sort-by="['object.name']"
          :items="settlementObjectsFiltered"
          :search="filterSearch"
          class="elevation-2"
        >
          <template v-slot:top class="pt-5 pb-3">
            <v-card class="elevation-0">
              <v-card-text class="pt-3">
                <v-row class="justify-end align-center table-filters">
                  <v-spacer></v-spacer>
                  <v-col cols="3">
                    <select-object hide-details :object-id.sync="settlementObjectsFilterData.objectId"/>
                  </v-col>
                  <v-col cols="3">
                    <search v-model="filterSearch" />
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>
          </template>

          <template #item.object.name="{ item }">
            <object-name class="mt-2 mb-1" :value="item.object" />
          </template>

          <template #item.provisionNet="{ item }">
            <currency-amount :amount="item.provisionNet" />
          </template>

          <template #item.provisionGross="{ item }">
            <currency-amount :amount="item.provisionGross" />
          </template>

          <template #item.saldo="{ item }">
            <currency-amount :amount="item.saldo" />
          </template>

          <template #item.actions="{ item }">
            <download-icon-button
              :loading="downloadingSettlementObjectPreviewPdf.includes(item.object.id)"
              @click="downloadSettlementObjectPreviewPdf(item.object.id)"
              :title="trans('settlement.downloadPdf')"
            />
          </template>
        </v-data-table>
      </template>
    </div>
  </v-container>
</template>

<script>
import {LocaleUtils} from '../utils/locale-utils';
import {DateUtils} from '../utils/date-utils';
import {RouterUtils} from '../utils/router-utils';
import {MainUtils} from '@/utils/main-utils';
import CurrencyAmount from '../components/CurrencyAmount';
import {SettlementApi} from "@/api/settlement-api";
import SelectObject from '@/components/SelectObject';
import Search from '@/components/Search';
import StatNumber from '@/components/StatNumber';
import dayjs from 'dayjs';
import PageHeading from '@/components/PageHeading';
import BackButton from '@/components/BackButton';
import ObjectName from '@/components/ObjectName';
import DateSelect from '../components/DateSelect';
import DownloadIconButton from '../components/DownloadIconButton';

export default {
  name: 'SettlementPreviewPage',
  mixins: [
    MainUtils,
    LocaleUtils,
    RouterUtils,
    DateUtils
  ],
  components: {
    DownloadIconButton,
    DateSelect,
    ObjectName,
    BackButton,
    PageHeading,
    SelectObject,
    Search,
    StatNumber,
    CurrencyAmount
  },
  data () {
    return {
      dateInternal: null,
      settlementPreview: null,
      generatingPreview: false,
      filterSearch: null,
      settlementObjectsFilterData: {
        objectId: null
      },
      downloadingSettlementObjectPreviewPdf: []
    }
  },
  computed: {
    pageTitle () {
      return this.trans('settlement.preview');
    },
    pageSubTitle () {
      if (this.settlementPreview?.year && this.settlementPreview?.month) {
        return this.getMonthNameByIndex(this.settlementPreview.month) + ' ' +  this.settlementPreview.year + ' - ' + this.transSingular('settlement.settlement', 1);
      }
      return null;
    },
    settlementObjectsFiltered () {
      return this.settlementObjectsFilterData.objectId
        ? this.settlementPreview?.settlementObjects.filter((item) => {
          return item.object.id === this.settlementObjectsFilterData.objectId;
        })
        : this.settlementPreview?.settlementObjects;
    }
  },
  created () {
    this.dateInternal = dayjs().format('YYYY-MM');
  },
  methods: {
    generatePreview () {
      this.generatingPreview = true;
      let date = dayjs(this.dateInternal);
      SettlementApi.getSettlementPreview(date.year(), date.month() + 1)
        .then(response => {
          this.settlementPreview = null;
          this.$nextTick(() => {
            this.settlementPreview = response.body;
          });
        })
        .catch(this.handleErrorResponse)
        .finally(() => this.generatingPreview = false)
      ;
    },

    downloadSettlementObjectPreviewPdf (objectId) {
      let date = dayjs(this.dateInternal);
      this.downloadingSettlementObjectPreviewPdf.push(objectId);
      SettlementApi.getSettlementPreviewPdf(date.year(), date.month() + 1, objectId)
        .then(this.downloadResponseAsFile)
        .catch(this.handleErrorResponse)
        .finally(() => {
          let index = this.downloadingSettlementObjectPreviewPdf.indexOf(objectId);
          if (index >= 0) {
            this.downloadingSettlementObjectPreviewPdf.splice(index, 1);
          }
        });
    }
  }
}
</script>

<style>
tr.open-commissions-preview-row td {
  background: #F9F9F9;
  color: #888;
  border-bottom: 2px dashed #CCC !important;
}
</style>
